/**
 * Образы (§12).
 *
 * Образ — сохранённый вариант внешнего вида. Создаётся из описания, из фото
 * или из их комбинации (§12.1). Варианты не перетирают друг друга: каждый
 * стоил единиц (§12.2).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Star, Trash2 } from 'lucide-react';
import { AspectRatio, lookPresets, type LookView } from '@alterium/shared';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { Input } from '../components/ui/Input';
import { PageHeader } from '../components/ui/PageHeader';
import { looksApi } from '../api/looks';
import { useTranslation } from 'react-i18next';
import { errorText } from '../i18n/errors';

export function LooksPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  // Локация — место без людей: она уходит фоном сцены, где человек уже свой.
  const [isLocation, setIsLocation] = useState(false);
  // Формат кадра: локация уходит фоном сцены и обязана совпадать с роликом.
  const [aspectRatio, setAspectRatio] = useState<string>(AspectRatio.R9X16);

  const looks = useQuery({
    queryKey: ['looks'],
    queryFn: looksApi.list,
    // Генерация идёт минуты — опрашиваем, пока что-то в работе.
    refetchInterval: (query) =>
      query.state.data?.some((look) => look.status === 'generating') ? 4000 : false,
    refetchOnWindowFocus: true,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['looks'] });

  const create = useMutation({
    mutationFn: () =>
      looksApi.create({
        name: name.trim(),
        prompt: prompt.trim() || undefined,
        isLocation,
        aspectRatio,
        count: 2,
      }),
    onSuccess: async () => {
      setCreating(false);
      setName('');
      setPrompt('');
      setIsLocation(false);
      setAspectRatio(AspectRatio.R9X16);
      await refresh();
    },
  });

  const update = useMutation({
    mutationFn: (params: { id: string; patch: Parameters<typeof looksApi.update>[1] }) =>
      looksApi.update(params.id, params.patch),
    onSuccess: refresh,
  });

  const remove = useMutation({ mutationFn: looksApi.remove, onSuccess: refresh });
  const more = useMutation({
    mutationFn: (id: string) => looksApi.variants(id, { count: 1 }),
    onSuccess: refresh,
  });

  const items = looks.data ?? [];

  return (
    <div>
      <PageHeader
        title={t('nav.looks')}
        description={t('looks.subtitle')}
        action={
          <Button variant="primary" onClick={() => setCreating((open) => !open)}>
            <Plus className="h-4 w-4" strokeWidth={2} />{t('looks.create')}</Button>
        }
      />

      {creating ? (
        <Card className="mb-6">
          <CardBody className="flex flex-col gap-4">
            {create.isError ? (
              <Alert tone="error">{errorText(create.error)}</Alert>
            ) : null}

            <Input
              label={t('common.name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={t('looks.namePlaceholder')}
            />

            <label className="flex flex-col gap-1.5">
              <span className="text-meta text-content-secondary">{t('common.description')}</span>
              <textarea
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                rows={3}
                placeholder={t('looks.descriptionPlaceholder')}
                className="resize-none rounded-control border border-line bg-surface-secondary px-3 py-2.5 text-body text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
              />
            </label>

            <label className="flex items-start gap-2.5">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={isLocation}
                onChange={(event) => setIsLocation(event.target.checked)}
              />
              <span className="text-meta text-content-secondary">
                {t('looks.isLocation')}
                <span className="block text-content-muted">{t('looks.isLocationHint')}</span>
              </span>
            </label>

            <div className="flex flex-wrap gap-2">
              {[AspectRatio.R9X16, AspectRatio.R16X9, AspectRatio.R1X1].map((ratio) => (
                <button
                  key={ratio}
                  type="button"
                  onClick={() => setAspectRatio(ratio)}
                  className={
                    ratio === aspectRatio
                      ? 'rounded-control border border-accent px-3 py-1.5 text-meta text-accent'
                      : 'rounded-control border border-line px-3 py-1.5 text-meta text-content-secondary transition-colors duration-fast hover:border-line-strong hover:text-content'
                  }
                >
                  {ratio}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {lookPresets.map((preset) => (
                <button
                  key={preset.name}
                  type="button"
                  onClick={() => {
                    setName(preset.name);
                    setPrompt(preset.prompt);
                  }}
                  className="rounded-control border border-line px-3 py-1.5 text-meta text-content-secondary transition-colors duration-fast hover:border-line-strong hover:text-content"
                >
                  {preset.name}
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                variant="primary"
                onClick={() => create.mutate()}
                disabled={create.isPending || name.trim().length === 0}
              >
                {create.isPending ? t('common.creating') : t('common.create')}
              </Button>
              <Button variant="tertiary" onClick={() => setCreating(false)}>{t('common.cancel')}</Button>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {items.length === 0 && !looks.isLoading ? (
        <EmptyState
          title={t('looks.empty.title')}
          description={t('looks.empty.description')}
          action={
            <Button variant="primary" onClick={() => setCreating(true)}>{t('looks.create')}</Button>
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((look) => (
            <LookCard
              key={look.id}
              look={look}
              onSelect={(variantId) =>
                update.mutate({ id: look.id, patch: { selectedVariantId: variantId } })
              }
              onPrimary={() => update.mutate({ id: look.id, patch: { isPrimary: true } })}
              onMore={() => more.mutate(look.id)}
              onDelete={() => remove.mutate(look.id)}
              busy={more.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function LookCard({
  look,
  onSelect,
  onPrimary,
  onMore,
  onDelete,
  busy,
}: {
  look: LookView;
  onSelect: (variantId: string) => void;
  onPrimary: () => void;
  onMore: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="group overflow-hidden rounded-card border border-line bg-surface">
      <div className="relative aspect-[3/4] bg-surface-secondary">
        {look.previewUrl ? (
          <img src={look.previewUrl} alt={look.name} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-meta text-content-muted">
            {t(`enum.lookStatus.${look.status}`, { defaultValue: look.statusLabel })}
          </div>
        )}

        {look.isPrimary ? (
          <span className="absolute left-2 top-2 rounded bg-page/80 px-1.5 py-0.5 text-[11px] text-content">{t('looks.primary')}</span>
        ) : null}

        {/* §62: действия по hover, а не постоянно на карточке */}
        <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity duration-fast group-hover:opacity-100">
          {!look.isPrimary ? (
            <button
              type="button"
              onClick={onPrimary}
              title={t('looks.makePrimary')}
              aria-label={t('looks.makePrimary')}
              className="rounded-control bg-page/80 p-1.5 text-content-secondary transition-colors duration-fast hover:text-content"
            >
              <Star className="h-4 w-4" strokeWidth={1.75} />
            </button>
          ) : null}
          <button
            type="button"
            onClick={onDelete}
            title={t('common.delete')}
            aria-label={t('common.delete')}
            className="rounded-control bg-page/80 p-1.5 text-content-secondary transition-colors duration-fast hover:text-error"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2 px-3 py-2.5">
        <p className="truncate text-meta text-content">{look.name}</p>

        {look.variants.length > 1 ? (
          <div className="flex gap-1.5">
            {look.variants.slice(0, 4).map((variant) => (
              <button
                key={variant.id}
                type="button"
                onClick={() => onSelect(variant.id)}
                aria-label={t('looks.chooseVariant')}
                className={
                  variant.id === look.selectedVariantId
                    ? 'h-8 w-6 overflow-hidden rounded border border-accent'
                    : 'h-8 w-6 overflow-hidden rounded border border-line opacity-60 transition-opacity duration-fast hover:opacity-100'
                }
              >
                {variant.url ? (
                  <img src={variant.url} alt="" className="h-full w-full object-cover" />
                ) : null}
              </button>
            ))}
          </div>
        ) : null}

        <button
          type="button"
          onClick={onMore}
          disabled={busy || look.status === 'generating'}
          className="text-left text-[11px] text-content-muted transition-colors duration-fast hover:text-content disabled:opacity-40"
        >
          {look.status === 'generating' ? t('common.generating') : t('looks.moreVariant')}
        </button>
      </div>
    </div>
  );
}
